import { Component, OnInit, OnDestroy, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { DeviceService } from '../../services/device.service';
import { Device } from '../../models/device.model';
import { ThinkingOrbComponent } from '../thinking-orb/thinking-orb.component';
import { delay } from 'rxjs/operators';
import { SpaceContextService, SpaceContext } from '../../services/space-context.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-devices',
  standalone: true,
  imports: [CommonModule, ThinkingOrbComponent],
  template: `
    <div class="p-8 max-w-7xl mx-auto">
      <header class="mb-8 border-l-4 border-zaxis-accent pl-4">
        <h1 class="text-3xl font-mono font-black tracking-tight text-white uppercase">Dispositivos</h1>
        <p class="text-gray-400 mt-2 text-[10px] font-mono tracking-widest uppercase">Dispositivos XR vinculados a tu cuenta</p>
      </header>

      <div *ngIf="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <!-- Custom Brutalist Skeleton Loading State -->
        <div *ngFor="let i of [1,2,3,4,5,6]" class="skeleton-box p-5 h-[220px] flex flex-col justify-between">
          <div>
            <div class="flex justify-between items-start mb-4">
              <div class="skeleton-text w-1/2"></div>
              <div class="skeleton-text w-1/4"></div>
            </div>
            <div class="skeleton-text title w-3/4"></div>
          </div>
          
          <div class="space-y-2 mb-4">
            <div class="skeleton-text h-[20px] w-full" style="height: 20px; margin: 0"></div>
            <div class="skeleton-text h-[20px] w-5/6" style="height: 20px; margin: 0"></div>
          </div>
          
          <div class="mt-4 pt-4 flex gap-2">
            <div class="skeleton-text flex-1" style="margin: 0; height: 32px;"></div>
            <div class="skeleton-text w-12" style="margin: 0; height: 32px;"></div>
          </div>
        </div>
      </div>

      <div *ngIf="!loading && devices.length === 0" class="text-center py-20 bg-black rounded-none border border-zaxis-border">
        <div class="flex justify-center mb-4">
          <i class="ph ph-device-mobile text-6xl text-gray-600"></i>
        </div>
        <p class="text-gray-400 text-sm font-mono uppercase tracking-widest">No hay dispositivos vinculados aún.</p>
        <p class="text-gray-500 text-xs font-mono uppercase tracking-widest mt-2">Usa el botón "Vincular Dispositivo" en el menú lateral.</p>
      </div>

      <div *ngIf="!loading && devices.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div *ngFor="let d of devices"
             class="bg-black border border-zaxis-border rounded-none p-5 hover:border-zaxis-accent transition-all group">

          <!-- Header -->
          <div class="flex items-start justify-between mb-4 border-b border-zaxis-border pb-4">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-none bg-black flex items-center justify-center border border-zaxis-border">
                <i class="ph ph-goggles text-xl text-zaxis-accent"></i>
              </div>
              <div>
                <h3 class="font-mono font-bold text-sm uppercase tracking-widest text-white group-hover:text-zaxis-accent transition-colors">{{ d.name || 'Sin nombre' }}</h3>
                <div class="flex items-center gap-1.5 mt-0.5">
                  <p class="text-[9px] text-gray-500 font-mono uppercase tracking-widest">{{ d.device_serial || d.device_id }}</p>
                  <button (click)="copyId(d.device_serial || d.device_id)" class="text-gray-500 hover:text-zaxis-accent transition-colors">
                    <i class="ph text-[10px]" [ngClass]="copiedId === (d.device_serial || d.device_id) ? 'ph-check text-zaxis-success' : 'ph-copy'"></i>
                  </button>
                </div>
              </div>
            </div>
            <span class="px-2 py-0.5 rounded-none text-[9px] font-mono font-bold uppercase tracking-widest border"
                  [class]="d.status === 'active' ? 'bg-zaxis-success/10 text-zaxis-success border-zaxis-success' : 'bg-black text-gray-400 border-zaxis-border'">
              {{ d.status }}
            </span>
          </div>

          <!-- Details -->
          <div class="space-y-2 text-[10px] font-mono uppercase tracking-widest">
            <div class="flex justify-between text-gray-500">
              <span>Registrado</span>
              <span class="text-gray-300">{{ d.registered_at | date:'mediumDate' }}</span>
            </div>
            <div *ngIf="d.updated_at" class="flex justify-between text-gray-500">
              <span>Última actualización</span>
              <span class="text-gray-300">{{ d.updated_at | date:'mediumDate' }}</span>
            </div>
            <div *ngIf="d.pairing_code" class="flex justify-between text-gray-500">
              <span>Código</span>
              <span class="text-gray-300">{{ d.pairing_code }}</span>
            </div>
          </div>

          <!-- Actions -->
          <div class="mt-4 pt-4 border-t border-zaxis-border flex gap-2">
            <button (click)="toggleStatus(d)" class="flex-1 px-3 py-2 border text-[10px] font-mono font-bold uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                    [class]="d.status === 'active' ? 'border-gray-600 text-gray-400 hover:text-white hover:border-white' : 'border-zaxis-success text-zaxis-success hover:bg-zaxis-success/10'">
              <i class="ph text-lg" [ngClass]="d.status === 'active' ? 'ph-pause' : 'ph-play'"></i>
              {{ d.status === 'active' ? 'Suspender' : 'Activar' }}
            </button>
            <button (click)="unlinkDevice(d)" class="flex-none px-3 py-2 border border-zaxis-danger/30 text-zaxis-danger/70 hover:text-zaxis-danger hover:border-zaxis-danger hover:bg-zaxis-danger/10 transition-colors flex items-center justify-center" title="Desvincular">
              <i class="ph ph-trash text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class DevicesComponent implements OnInit, OnDestroy {
  private deviceService = inject(DeviceService);
  private platformId = inject(PLATFORM_ID);
  public spaceContext = inject(SpaceContextService);

  devices: Device[] = [];
  loading = true;
  copiedId: string | null = null;
  
  private contextSubscription?: Subscription;
  private currentContextId?: string;

  ngOnInit() {
    this.contextSubscription = this.spaceContext.activeContext$.subscribe(context => {
      if (context) {
        this.currentContextId = context.id;
        this.loadDevices();
      }
    });
  }

  ngOnDestroy() {
    this.contextSubscription?.unsubscribe();
  }

  copyId(id: string | undefined) {
    if (!id) return;
    if (isPlatformBrowser(this.platformId)) {
      navigator.clipboard.writeText(id).then(() => {
        this.copiedId = id;
        setTimeout(() => {
          if (this.copiedId === id) this.copiedId = null;
        }, 2000);
      }).catch(err => console.error('Error copying text: ', err));
    }
  }

  loadDevices() {
    this.loading = true;
    const stream = this.deviceService.getDevices(this.currentContextId);
    const delayedStream = isPlatformBrowser(this.platformId) ? stream.pipe(delay(500)) : stream;
    
    delayedStream.subscribe({
      next: (res) => {
        this.devices = res.devices || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading devices', err);
        this.loading = false;
      }
    });
  }

  toggleStatus(device: Device) {
    const newStatus = device.status === 'active' ? 'suspended' : 'active';
    this.deviceService.toggleDeviceStatus(device.id, newStatus).subscribe({
      next: () => {
        // Optimistic update
        device.status = newStatus;
      },
      error: (err) => {
        console.error('Error toggling status', err);
      }
    });
  }

  unlinkDevice(device: Device) {
    if (confirm(`¿Estás seguro de que deseas desvincular el dispositivo "${device.name}"?`)) {
      this.deviceService.unlinkDevice(device.id).subscribe({
        next: () => {
          this.devices = this.devices.filter(d => d.id !== device.id);
        },
        error: (err) => {
          console.error('Error unlinking device', err);
        }
      });
    }
  }
}
